import { Box, Divider, Tooltip, Typography } from "@mui/material";
import * as React from "react";
import { DragDropContext, Draggable, DropResult, Droppable, ResponderProvided } from "react-beautiful-dnd";
import { Base } from "ui/Scripts/source/framework/base";
import { ApplicationPermissionType, ConfirmationDialogResult, ContentPagePanelType, EnumHelper, UserGroupType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { IPagePanel, PagePanel } from "../../../models/common/pagePanel";
import { PagePanelsDrawer } from "../../framework/Page/PagePanelsDrawer/PagePanelsDrawer";
import { FrontPanelPropertiesDrawer } from "../../front/FrontPanelPropertiesDrawer";
import { AddButton } from "source/components/framework/Button/AddButton";
import { AppButton } from "ui/Scripts/source/components/framework/Button/AppButton";
import { AppShareIcon } from "source/components/framework/Icon/AppShareIcon";
import { SelectUserGroupDialog } from "source/components/userGroup/SelectUserGroupDialog";
import { IUserGroupListItem } from "ui/Scripts/source/models/userGroup/userGroupListItem";
import { AppUtils } from "source/framework/appUtils";
import { UserGroupListItems } from "ui/Scripts/source/models/userGroup/userGroupListItems";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { SharedPanels } from "source/models/panel/sharedPanels";
import { ISharedPanel, SharedPanel, ISharedPanelSave, SharedPanelSave } from "source/models/panel/sharedPanel";
import { useEffect } from "react";
import { StoreUtils } from "source/models/store/storeUtils";
import { SelectSharedPanelDrawer } from "./SelectSharedPanelDrawer";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";
import { IModifyUser, ModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { ManageSharedPanelsDrawer } from "./ManageSharedPanelsDrawer";

interface IDashboardPanelListProps {
    companyId: string;
    applicationPermissionType?: ApplicationPermissionType;
    hidden: boolean;
    panels: IPagePanel[];
    isFrontPage?: boolean;
    canSharePanels?: boolean;
    enableShowInFrontPage?: boolean;
    shareTitle?: string;
    onUpdatePanels: (panels: IPagePanel[]) => void;
    onSavePanels: (panels: IPagePanel[]) => void;
    onToggleMenuPanel: () => void;
    onShowChartPanelProperties?: (panelId: string) => void;
    onAddChart?: (sharedPanel: ISharedPanel) => void;
    onFrontPagePanelProperyClick?: (id: string, panelType: number, panelSize: number, visible: boolean, title: string) => void;
    onGetSharedPanels?: (companyId: string) => Promise<SharedPanels>;
    onSaveSharedPanel?: (companyId: string, orgSharedPanelId: string, sharedPanel: ISharedPanelSave) => Promise<ApiSuccess>;
    onGetUserGroupSelectListItems?: (companyId: string, pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, groupType: number,
        ignoreUserGroupCodes: string[], requireUserGroupCodes: string[], ignoreQrCodeUserGroups: boolean, requireQrCodeUserGroups: boolean, messageTargetMode: boolean) => Promise<UserGroupListItems>;
}

interface IDashboardPanelListState {
    showFrontPanelPropertiesDialog: boolean;
    panelPropertiesDialogTitle: string;
    panelPropertiesDialogType: number;
    panelPropertiesDialogSize: number;
    panelPropertiesDialogVisible: boolean;
    panelPropertiesDialogShowInFrontPage: boolean;
    sharedPanels: ISharedPanel[];
    currentUser: IModifyUser;
    showSelectUserGroupsDialog: boolean;
    selectUserGroupsDialogPanel: IPagePanel;
    selectUserGroupsDialogInitiallySelectedUserGroupCodes: string[];
    showSelectSharedPanel: boolean;
    showManageSharedPanelsDrawer: boolean;
}

export const DashboardPanelList = (props: IDashboardPanelListProps) => {
    const [state, setState] = React.useState<IDashboardPanelListState>({
        showFrontPanelPropertiesDialog: false,
        panelPropertiesDialogTitle: "",
        panelPropertiesDialogType: 0,
        panelPropertiesDialogSize: 1,
        panelPropertiesDialogVisible: false,
        panelPropertiesDialogShowInFrontPage: false,
        sharedPanels: [],
        currentUser: StoreUtils.getNowModifyUser(),
        showSelectUserGroupsDialog: false,
        selectUserGroupsDialogPanel: null,
        selectUserGroupsDialogInitiallySelectedUserGroupCodes: [],
        showSelectSharedPanel: false,
        showManageSharedPanelsDrawer: false
    });
    
    const loadSharedPanels = async () => {
        const result = await AppUtils.callService(() => props.onGetSharedPanels(props.companyId), false);
        setState(old => ({
            ...old,
            sharedPanels: result.panels
        }));
    };

    useEffect(() => {
        if (props.hidden || !props.companyId || !props.onGetSharedPanels) return;
        loadSharedPanels();
    }, [props.hidden, props.companyId, props.onGetSharedPanels]);

    const savePanels = async (panels: IPagePanel[]) => {
        await props.onSavePanels(panels);
        setState(old => ({
            ...old,
            showFrontPanelPropertiesDialog: false
        }));
    };

    const getPanelOptionItemStyle = (isDragging: boolean, draggableStyle: any) => {
        return {
            userSelect: "none",
            ...draggableStyle
        };
    };

    const handlePanelCheckBoxDragEnd = (result: DropResult, provided: ResponderProvided) => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }
        const panels = PagePanel.swapPagePanels(props.panels, result.source.index, result.destination.index);
        for (let i = 0; i < panels.length; i++) {
            panels[i].position = i;
        }
        props.onUpdatePanels(panels);
        savePanels(panels);
    };

    const handleSavePanelSize = (panelType: number, panelSize: number, visible: boolean, showInFrontPage: boolean) => {
        const panels = props.panels.slice(0);
        const panel = panels.find(i => Base.equalInt(i.panelType, panelType));
        if (!panel) return;
        panel.size = panelSize;
        panel.visible = visible;
        panel.showInFrontPage = showInFrontPage ?? false;
        savePanels(panels);
    };
    
    const handlePropertiesClick = (id: string, panelType: number, panelSize: number, visible: boolean, title: string, showInFrontPage: boolean) => {
        if (props.isFrontPage) {
            props.onFrontPagePanelProperyClick(id, panelType, panelSize, visible, title);
            return;
        }
        if (!EnumHelper.isEqual(panelType, ContentPagePanelType.Chart)) {
            setState(old => ({
                ...old,
                showFrontPanelPropertiesDialog: true,
                panelPropertiesDialogType: panelType,
                panelPropertiesDialogSize: panelSize,
                panelPropertiesDialogVisible: visible,
                panelPropertiesDialogTitle: title,
                panelPropertiesDialogShowInFrontPage: showInFrontPage ?? false
            }));
        } else if (props.onShowChartPanelProperties) {
            props.onShowChartPanelProperties(id);
        }
    };

    const handleFrontPanelPropertiesDialogCancel = () => {
        setState(old => ({
            ...old,
            showFrontPanelPropertiesDialog: false
        }));
    };

    // #region Share Panel
    const handleSharePanel = (event: React.MouseEvent, panel: IPagePanel) => {
        if (!panel) return;
        const panelId = panel.getId();
        event.stopPropagation();
        setState(old => ({
            ...old,
            showSelectUserGroupsDialog: true,
            selectUserGroupsDialogPanel: panel,
            selectUserGroupsDialogInitiallySelectedUserGroupCodes: old.sharedPanels.find(i => i.id === panelId)?.userGroups.map(i => i.code) ?? []
        }));
    };

    const handleSelectUserGroupsDialogOk = async (userGroups: IUserGroupListItem[], userGroupType: UserGroupType) => {
        const panelId = state.selectUserGroupsDialogPanel.getId();
        const sharedPanel = state.sharedPanels.find(i => i.id === panelId);
        // Remove shared panel if no user groups selected
        if (sharedPanel && (!userGroups || userGroups.length < 1)) {
            if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveSharedPanelParameter, sharedPanel.id), false) === ConfirmationDialogResult.Yes) {
                const result = await AppUtils.callService(() => props.onSaveSharedPanel(props.companyId, sharedPanel.id, null), false);
                if (!result) return;
                setState((old) => ({
                    ...old,
                    showSelectUserGroupsDialog: false,
                    sharedPanels: old.sharedPanels.filter(i => i.id !== sharedPanel.id)
                }));
            }
            return;
        }
        //Add or change shared panel used groups
        if (!userGroups || userGroups.length < 1) return;
        const newUserGroups = userGroups.map(user => {
            const newUser = new ModifyUser();
            newUser.code = user.code;
            newUser.name = user.name;
            newUser.picture = user.picture;
            newUser.date = userGroupType;
            return newUser;
        });
        // Check if panel is already shared
        if (sharedPanel) {
            if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouWantToReplaceExistingSharedPanelParameter, state.selectUserGroupsDialogPanel.getTitle())) !== ConfirmationDialogResult.Yes) {
                return;
            }
        }
        // Call server
        const newSharedPanel = new SharedPanel(state.selectUserGroupsDialogPanel);
        newSharedPanel.shareUser = new ModifyUser(state.currentUser);
        newSharedPanel.shareUser.date = Base.getNowTicks();
        newSharedPanel.userGroups = newUserGroups;
        const saveSharedPanel = new SharedPanelSave(newSharedPanel);
        const result = await AppUtils.callService(() => props.onSaveSharedPanel(props.companyId, sharedPanel?.id, saveSharedPanel));
        if (!result) return;
        setState(old => ({
            ...old,
            showSelectUserGroupsDialog: false,
            sharedPanels: [...old.sharedPanels.filter(i => i.id !== panelId), newSharedPanel]
        }));
    };

    const handleSelectUserGroupsDialogCancel = () => {
        setState(old => ({
            ...old,
            showSelectUserGroupsDialog: false
        }));
    };
    // #endregion Share Panel

    // #region Add Shared Panel
    const handleAddSharedPanel = () => {
        setState(old => ({
            ...old,
            showSelectSharedPanel: true
        }));
    };

    const handleSelectSharedPanelCancel = () => {
        setState(old => ({
            ...old,
            showSelectSharedPanel: false
        }));
    };

    const handleSelectSharedPanelOk = (sharedPanel: ISharedPanel) => {
        if (!sharedPanel) return;
        setState(old => ({
            ...old,
            showSelectSharedPanel: false
        }));
        props.onAddChart(sharedPanel);
    };
    // #endregion Add Shared Panel

    // #region Manage Shared Panels
    const handleManageSharedPanels = () => {
        setState(old => ({
            ...old,
            showManageSharedPanelsDrawer: true
        }));
    };

    const handleCloseManageSharedPanelsDrawer = () => {
        setState(old => ({
            ...old,
            showManageSharedPanelsDrawer: false
        }));
        loadSharedPanels();
    };
    // #endregion Manage Shared Panels

    const hasBeforeElement = props.onAddChart || props.canSharePanels && state.sharedPanels.length > 0;
    const notMySharedPanels = state.sharedPanels.filter(i => (i.shareUser?.code ?? "") !== (state.currentUser?.code ?? ""));

    return (
        <>
            <PagePanelsDrawer
                open={!props.hidden}
                onClose={props.onToggleMenuPanel}
                fitToContainer
            >
                <VerticalContainer
                    minHeight="100%"
                    before={hasBeforeElement
                        ? <>
                            <Box margin="1rem">
                                {!!props.onAddChart &&
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                        <AddButton
                                            onClick={() => props.onAddChart(null)}
                                            sx={{
                                                borderRadius: ".5rem",
                                                padding: "6px 0",
                                            }}
                                            fullWidth
                                        >
                                            <Typography>{Translations.AddPanel}</Typography>
                                        </AddButton>
                                        {notMySharedPanels.length > 0 &&
                                            <AddButton
                                                onClick={handleAddSharedPanel}
                                                sx={{
                                                    borderRadius: ".5rem",
                                                    padding: "6px 0",
                                                }}
                                                fullWidth
                                            >
                                                <Typography>{Translations.AddSharedPanel}</Typography>
                                            </AddButton>
                                        }
                                    </Box>
                                }
                                {(props.canSharePanels && state.sharedPanels.length > 0) &&
                                    <AppButton
                                        onClick={handleManageSharedPanels}
                                        sx={{
                                            borderRadius: ".5rem",
                                            padding: "6px 0",
                                            marginTop: props.onAddChart ? "1rem" : 0
                                        }}
                                        fullWidth
                                        variant="contained"
                                        startIcon={<AppShareIcon />}
                                    >
                                        <Typography>{Translations.ManageSharedPanels}</Typography>
                                    </AppButton>
                                }
                            </Box>
                            <Divider sx={{ margin: "1rem 0" }} />
                        </>
                        : undefined
                    }
                    after={
                        <>
                            <Divider sx={{ margin: "1rem 0" }} />
                            <Box display="flex" justifyContent="flex-end" margin="1rem">
                                <AppButton variant="outlined" onClick={props.onToggleMenuPanel}>
                                    {Translations.Close}
                                </AppButton>
                            </Box>
                        </>
                    }
                >
                    <DragDropContext onDragEnd={handlePanelCheckBoxDragEnd}>
                        <Droppable droppableId="panelOptionItems">
                            {(provided, snapshot) => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    style={{ height: "100%", overflowY: "auto", padding: `${hasBeforeElement ? "0" : "1rem"} 24px 0` }}
                                >
                                    {props.panels.map((panel, index) => {
                                        if (!props.isFrontPage && (EnumHelper.isEqual(panel.panelType, ContentPagePanelType.SharedFiltersAndReports) || panel.getDisabled())) {
                                            return null;
                                        }
                                        const sharedPanel = state.sharedPanels.find(i => i.id === panel.getId());
                                        const sharedPanelHasUserGroups = sharedPanel && sharedPanel.userGroups && sharedPanel.userGroups.length > 0;
                                        return <Draggable key={panel.getUniqueId()} draggableId={panel.getUniqueId()} index={index}>
                                            {(provided, snapshot) => (
                                                <Box
                                                    onClick={() => handlePropertiesClick(panel.getId(), panel.panelType, panel.size, panel.visible, panel.getTitle(), panel.showInFrontPage)}
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={getPanelOptionItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                                                    sx={{
                                                        borderRadius: ".5rem",
                                                        border: "1px solid",
                                                        borderColor: "divider",
                                                        backgroundColor: panel.visible ? "action.selected" : "action.hover",
                                                        marginBottom: "1rem",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        padding: "6px 0"
                                                    }}
                                                >
                                                    <Tooltip title={panel.visible ? Translations.PanelVisible : ""}>
                                                        <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                                                            <Box margin="0 8px" sx={{ color: panel.visible ? "text.secondary" : "text.disabled" }}>{panel.getIcon()}</Box>
                                                            <Typography sx={{ color: panel.visible ? "text.secondary" : "text.disabled" }}>{panel.getTitle()}</Typography>
                                                        </Box>
                                                    </Tooltip>
                                                    {(props.canSharePanels && panel.panelType === ContentPagePanelType.Chart) &&
                                                        <Tooltip title={sharedPanel ? (sharedPanelHasUserGroups ? Translations.PanelIsShared : Translations.PanelIsSharedWithoutUserGroups) : Translations.SharePanel}>
                                                            <Box
                                                                margin="0 8px 0 auto"
                                                                sx={{ color: panel.visible ? "text.secondary" : "text.disabled", cursor: "pointer" }}
                                                                onMouseEnter={(e) => e.stopPropagation()}
                                                                onClick={(e) => handleSharePanel(e, panel)}
                                                            >
                                                                <AppShareIcon color={sharedPanel ? (sharedPanelHasUserGroups ? "success" : "warning") : undefined} />
                                                            </Box>
                                                        </Tooltip>
                                                    }
                                                </Box>
                                            )}
                                        </Draggable>;
                                    })}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </VerticalContainer>
            </PagePanelsDrawer>
            {state.showSelectSharedPanel &&
                <SelectSharedPanelDrawer
                    companyId={props.companyId}
                    title={Translations.SelectSharedPanel}
                    sharedPanels={notMySharedPanels}
                    onOk={handleSelectSharedPanelOk}
                    onCancel={handleSelectSharedPanelCancel}
                />
            }
            {state.showFrontPanelPropertiesDialog &&
                <FrontPanelPropertiesDrawer
                    title={state.panelPropertiesDialogTitle}
                    panelSize={state.panelPropertiesDialogSize}
                    panelType={state.panelPropertiesDialogType}
                    visible={state.panelPropertiesDialogVisible}
                    showInFrontPage={state.panelPropertiesDialogShowInFrontPage}
                    enableShowInFrontPage={props.enableShowInFrontPage && state.panelPropertiesDialogType === ContentPagePanelType.Statistics}
                    onOk={handleSavePanelSize}
                    onCancel={handleFrontPanelPropertiesDialogCancel}
                />
            }
            {state.showSelectUserGroupsDialog &&
                <SelectUserGroupDialog
                    companyId={props.companyId}
                    title={String.format(Translations.SharePanelParametrToUserGroups, state.selectUserGroupsDialogPanel.getTitle())}
                    initiallySelectedUserGroupCodes={state.selectUserGroupsDialogInitiallySelectedUserGroupCodes}
                    multiSelect
                    listModeChangeDisabled
                    enableZeroSelection={state.selectUserGroupsDialogInitiallySelectedUserGroupCodes.length > 0}
                    onOk={handleSelectUserGroupsDialogOk}
                    onCancel={handleSelectUserGroupsDialogCancel}
                    onGetUserGroupListItems={props.onGetUserGroupSelectListItems}
                />
            }
            {state.showManageSharedPanelsDrawer &&
                <ManageSharedPanelsDrawer
                    companyId={props.companyId}
                    sharedPanels={state.sharedPanels}
                    onCancel={handleCloseManageSharedPanelsDrawer}
                    onSaveSharedPanel={props.onSaveSharedPanel}
                    onGetUserGroupSelectListItems={props.onGetUserGroupSelectListItems}
                />
            }
        </>
    );
};
